import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Compass, MapPin, ArrowUpRight } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import { EVENTS, EVENT_TYPES, DESTINATIONS } from "@/data/platform";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const TYPE_COLORS = { run: "#C57B57", yoga: "#89937C", coffee: "#8A5236", cycle: "#737D67", trek: "#5C665A", festival: "#AD6A48" };

export default function WellnessCalendar() {
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [type, setType] = useState("all");
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const events = EVENTS.filter((e) => e.month === month && (type === "all" || e.type === type));

  return (
    <div className="min-h-screen bg-[#F7F5F0] text-[#2D372B]">
      <Nav />
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-20 md:pb-28">
        <div className="max-w-2xl mb-12">
          <div className="overline mb-4">India Wellness Calendar</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05]">
            The right month changes <span className="italic text-[#AD6A48]">everything.</span>
          </h1>
          <p className="mt-5 text-[#5C665A] leading-relaxed">Marathons, yoga festivals, coffee harvests, trekking windows — browse by month and build your journey around a moment worth travelling for.</p>
        </div>

        <div className="flex flex-wrap gap-2 mb-5" data-testid="calendar-months">
          {MONTHS.map((m, i) => (
            <button key={m} data-testid={`month-${m.toLowerCase()}`} onClick={() => setMonth(i + 1)}
              className={`rounded-full px-5 py-2.5 text-sm font-medium border transition-colors ${month === i + 1 ? "bg-[#2D372B] border-[#2D372B] text-[#F7F5F0]" : "border-[#E5E2D9] bg-white text-[#5C665A] hover:border-[#89937C]"}`}>
              {m}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mb-12" data-testid="calendar-types">
          {[["all", "All"], ...Object.entries(EVENT_TYPES)].map(([k, label]) => (
            <button key={k} data-testid={`type-${k}`} onClick={() => setType(k)}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-wider border transition-colors ${type === k ? "bg-[#C57B57] border-[#C57B57] text-white" : "border-[#E5E2D9] bg-white text-[#5C665A] hover:border-[#C57B57]"}`}>
              {label}
            </button>
          ))}
        </div>

        {events.length === 0 ? (
          <p data-testid="calendar-empty" className="text-[#5C665A] py-14 text-center">No {type !== "all" ? EVENT_TYPES[type].toLowerCase() : ""} events listed for {MONTHS[month - 1]} — try another filter.</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {events.map((e) => {
              const d = e.dest && DESTINATIONS.find((x) => x.slug === e.dest);
              return (
                <div key={e.name} data-testid="calendar-event-card" className="bg-white border border-[#E5E2D9] rounded-3xl p-7 flex flex-col">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1 rounded-full text-white" style={{ backgroundColor: TYPE_COLORS[e.type] }}>{EVENT_TYPES[e.type]}</span>
                    <span className="inline-flex items-center gap-1.5 text-xs text-[#5C665A]"><CalendarDays size={12} /> {e.timing}</span>
                  </div>
                  <h3 className="font-serif-display text-2xl leading-snug">{e.name}</h3>
                  <p className="text-sm text-[#89937C] font-medium mt-1 inline-flex items-center gap-1"><MapPin size={12} /> {e.location}</p>
                  <p className="text-sm text-[#5C665A] mt-3 leading-relaxed flex-1">{e.note}</p>
                  {d && (
                    <Link to={`/destinations/${d.slug}`} className="mt-4 inline-flex items-center gap-1.5 text-sm text-[#AD6A48] font-medium hover:text-[#8A5236] transition-colors">
                      Explore {d.name} <ArrowUpRight size={13} />
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-16 bg-[#2D372B] rounded-3xl p-9 sm:p-12 text-center">
          <h2 className="font-serif-display text-3xl sm:text-4xl text-[#F7F5F0]">Found your moment?</h2>
          <p className="text-[#C9CFC0] mt-3 max-w-md mx-auto">Tell the AI planner which month you're travelling — it'll build the whole journey around it.</p>
          <Link to="/journey" data-testid="calendar-plan-cta" className="mt-7 inline-flex items-center gap-2 rounded-full px-8 py-3.5 bg-[#C57B57] text-white font-medium hover:bg-[#AD6A48] transition-colors">
            <Compass size={15} /> Plan around this event
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}
